import { DateTime } from 'luxon';

/* ---------------------------------------------------------
 * 1. formatName() — cleaned, fixed, more robust
 * --------------------------------------------------------- */

export const formatName = (input: string = ''): string => {
  if (!input) return '';

  // Take the part before comma, strip digits/spaces
  const cleaned = input.split(',')[0].replace(/\d+/g, '').trim();

  if (!cleaned) return '';

  const lookup: Record<string, string> = {
    apis: 'APIs',
    conference_sessions: 'Conference',
    data: 'Data Sets',
    learningpaths: 'Learning Paths',
    learningpath_items: 'Learning Path',
    openprojects: 'Open Projects',
    patterns: 'Patterns',
    techtalk: 'Tech Talks',
    gettingstarted: 'Getting Started',
  };

  if (lookup[cleaned]) return lookup[cleaned];

  // Capitalize each word
  return cleaned
    .replace(/[-_]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/* ---------------------------------------------------------
 * 2. formatDateString() — simplified & consistent
 * --------------------------------------------------------- */

export const formatDateString = (dateInput: string | Date, useUTC: boolean = false): string => {
  if (!dateInput) return '';

  let date: Date;

  if (typeof dateInput === 'string') {
    // Accept YYYY-MM-DD or full ISO
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      const [y, m, d] = dateInput.split('-').map(Number);
      date = new Date(Date.UTC(y, m - 1, d)); // safer for date-only inputs
    } else {
      date = new Date(dateInput);
    }
  } else {
    date = dateInput;
  }

  if (isNaN(date.getTime())) return '';

  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: useUTC ? 'UTC' : undefined,
  };

  return new Intl.DateTimeFormat('en-GB', options).format(date);
};

/* ---------------------------------------------------------
 * 3. formatDateTimeForTimezone() — improved Luxon version
 * --------------------------------------------------------- */

export const formatDateTimeForTimezone = (
  isoString: string | null | undefined,
  ianaZone?: string,
): string => {
  if (!isoString) return '';

  const zone = ianaZone?.trim() || 'UTC';

  const dt = DateTime.fromISO(isoString, { zone });

  if (!dt.isValid) return '';

  const datePart = dt.toFormat('d LLLL yyyy');
  const timePart = dt.toFormat('HH:mm');

  const offsetHours = dt.offset / 60;

  const offsetLabel =
    offsetHours === 0 ? 'UTC' : `GMT${offsetHours > 0 ? `+${offsetHours}` : offsetHours}`;

  return `${datePart} at ${timePart} ${offsetLabel}`;
};

/* ---------------------------------------------------------
 * 4. capitalizeFirstLetter() — strict + safe
 * --------------------------------------------------------- */

export const capitalizeFirstLetter = (str: string): string => {
  if (!str || typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/* ---------------------------------------------------------
 * 5. removeLeadingTrailingSlashes() — small fix
 * --------------------------------------------------------- */

export const removeLeadingTrailingSlashes = (str: string = ''): string =>
  str.replace(/^\/|\/$/g, '');
